import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { ProfileType } from '../../common/enums/ProfileType';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { and } from '../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../common/hooks/use-form-item/validators/operational';
import { PROFILE_MAX_NAME_LENGTH } from '../../organization/profiles/components/ProfileNameInput';
import { OrganizationProfilesService } from '../../organization/profiles/services/OrganizationProfilesService';
import { ButtonBox, PrimaryButton, TertiaryButton } from '../components/Buttons';

type Props = {
  profileId: number;
  profileType: ProfileType;
  currentName: string;

  onClosed: (profileId: number | undefined) => void;
};

export const UpdateProfileFlow = ({ profileId, profileType, currentName, onClosed }: Props) => {
  const [profileService] = useState(new OrganizationProfilesService());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      profileService.cancelAllRequests();
    };
  }, [profileService]);

  const [name, nameComponent, nameValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('updateProfile.name.input'),
    value: currentName,
    validator: and(required(), maxLength(PROFILE_MAX_NAME_LENGTH)),
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      showRequiredIndicator: true,
      focusedLabel: t('updateProfile.name.focused'),
    },
    textOptions: { trimOnBlur: true },
  });

  const canSubmit = !loading && nameValid;

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      if (name === currentName) {
        // Typed the same name or did not change it
        onClosed(undefined);
        return;
      }
      setLoading(true);
      await profileService.updateProfile(profileId, profileType, name);
      onClosed(profileId);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  return (
    <Dialog title={t('updateProfile.title')} onDismiss={() => onClosed(undefined)}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={handleSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          {nameComponent}
          <ButtonBox>
            <TertiaryButton disabled={loading} onClick={() => onClosed(undefined)}>
              {t('updateProfile.back')}
            </TertiaryButton>
            <PrimaryButton disabled={!canSubmit} type="submit">
              {t('updateProfile.submit')}
            </PrimaryButton>
          </ButtonBox>
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
